import { formatNumberIntl } from "../../../shared/utils/DateUtil";

/**
 * Calculates the EMI, principal amount, and total interest payable for a given loan.
 * 
 * @param loanAmount - The principal amount of the loan.
 * @param annualInterestRate - The annual interest rate (as a percentage).
 * @param loanTenure - The tenure of the loan. If `tenureType` is 0, this is in years. If `tenureType` is 1, this is in months.
 * @param tenureType - Indicates the type of tenure. 0 for years, 1 for months.
 * 
 * @returns An object containing the principal amount, EMI, and total interest payable.
 */
export function calculateLoanDetails(
    loanAmount: number, 
    annualInterestRate: number, 
    loanTenure: number, 
    tenureType: number
): { principalAmount: number, emiAmount: number, interestPayable: number } {
    // Convert annual interest rate percentage to decimal
    const annualRate = annualInterestRate / 100;
    
    // Convert annual interest rate to monthly interest rate
    const monthlyRate = annualRate / 12;
    

    if(!loanTenure){
      tenureType === 0 ?  loanTenure = 1 : loanTenure = 12;
    }

    // Convert loan tenure to number of monthly payments based on tenureType
    const totalPayments = tenureType === 0 
        ? loanTenure * 12 // Convert years to months
        : loanTenure; // Already in months
    
    // Calculate EMI using the formula
    const emi = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
    
    // Calculate total amount to be paid using EMI
    const totalAmount = emi * totalPayments;
    
    // Calculate total interest payable
    const totalInterest = totalAmount - loanAmount;
    
    return {
        principalAmount: Math.round(loanAmount) || 0,
        emiAmount: parseFloat(emi.toFixed(2)),
        interestPayable: parseFloat(totalInterest.toFixed(2))
    };
}



/**
 * Calculates the specified percentage of a given amount and returns the result rounded to one decimal place.
 * 
 * @param amount - The total amount from which the percentage is calculated.
 * @param percentage - The percentage of the total amount to calculate.
 * @returns The percentage of the amount, rounded to one decimal place.
 */
export function calculatePercentage(amount: number, percentage: number): number {
    // Calculate the percentage of the amount
    const result = amount * (percentage / 100);

    // Round the result to one decimal place and return it
    return parseFloat(result.toFixed(1));
}


export  function getAmountWarningMessage(
    loanAmount: string | number,
    minAmount: string | number,
    maxAmount: string | number
  ): string | null {

    // Convert inputs to numbers
    const numericLoanAmount = Number(loanAmount);
    const numericMinAmount = Number(minAmount);
    const numericMaxAmount = Number(maxAmount);
  
    // Check if the loan amount is out of the valid range
    if (numericLoanAmount > numericMaxAmount || numericLoanAmount < numericMinAmount) {
      return `Amount is out of offer range: ${formatNumberIntl(numericMinAmount)} to ${formatNumberIntl(numericMaxAmount)}`;
    }
  
    // Return null if no warning is needed
    return null;
};



export function showTenureMessage (
    loanTenure: number,
    tenureType: number,
    maxTenure: number | string,
    minTenure: number | string
  ): boolean {
    // Convert tenure values to numbers if they are not already
    const numericMaxTenure = Number(maxTenure);
    const numericMinTenure = Number(minTenure);
  
    // Yearly tenure adjustment
      if (tenureType === 0) {
        // Convert max and min tenure from months to years
        return loanTenure > numericMaxTenure / 12 || loanTenure < numericMinTenure / 12;
      } else {
        // Tenure is already in years
        return loanTenure > numericMaxTenure || loanTenure < numericMinTenure;
      } 
};


export function  getTotalDisbursalAmount(offerCalculation:any,offerDetails:any): string{
    // Helper function to safely convert values to numbers with a default of 0
    const toNumber = (value: any): number => {
      const number = Number(value);
      return isNaN(number) ? 0 : number;
    };
  
    // Safely extract and convert values
    const loanAmount: number = toNumber(offerCalculation.loanAmount);
    const processingFee: number = toNumber(offerDetails.processingFee);
    const percentage: number = 18; // Assuming this is a fixed percentage for calculation
  
    // Calculate percentage amounts safely
    const calculatePercentageSafely = (amount: number, percentage: number): number => {
      if (isNaN(amount) || isNaN(percentage)) return 0;
      return (amount * percentage) / 100;
    };
  
    const feeAmount: number = calculatePercentageSafely(loanAmount, processingFee);
    const percentageAmount: number = calculatePercentageSafely(feeAmount, percentage);

  
    // Calculate the total disbursal amount
    const totalAmount: number = loanAmount - percentageAmount - feeAmount;
  
    // Format the total amount directly as a number
    return formatNumberIntl(totalAmount);
  };


 /**
 * Converts a date string from "yyyy-mm-dd" or "yyyy-mm-ddTHH:MM:SS.sssZ" to "dd/mm/yyyy" format.
 * @param dateString - The date string in ISO 8601 or "yyyy-mm-dd" format.
 * @returns The formatted date string in "dd/mm/yyyy" format.
 */
export function formatDate (dateString: string): string {
  // Create a Date object from the input string
  const date = new Date(dateString);

  // Extract the day, month, and year
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getUTCFullYear();

  return `${day}-${month}-${year}`;
};

function toNumber(value?: number): number {
  return isNaN(value ?? NaN) || !isFinite(value ?? NaN) ? 0 : value ?? 0;
}

export function calculateOfferTotal(
  principalAmount?: number,
  interestPayable?: number,
  loanAmount?: number,
  processingFee?: number
): string {
  // Extract and validate the required values
  const validPrincipalAmount = toNumber(principalAmount);
  const validInterestPayable = toNumber(interestPayable);
  const validLoanAmount = toNumber(loanAmount);
  const validProcessingFee = toNumber(processingFee);

  // Calculate the processing fee amount and additional 18% on that fee
  const processingFeeAmount = calculatePercentage(validLoanAmount, validProcessingFee);
  const additional18Percent = processingFeeAmount * 0.18;

  // Calculate the total
  const total = validPrincipalAmount + validInterestPayable - additional18Percent - processingFeeAmount ;

  // Ensure total is a valid number
  const safeTotal = isNaN(total) || !isFinite(total) ? 0 : total;

  // Format the number to two decimal places
  return formatNumberIntl(parseFloat(safeTotal.toFixed(2)));
}